/**
 * PWA của web quầy: đăng ký service worker và nút "Cài lên màn hình chính".
 *
 * Chủ tiệm mở web quầy trên tablet đặt ở quầy và trên điện thoại. Cài lên màn hình chính thì mở
 * bằng một chạm, toàn màn hình, không thanh địa chỉ — như một ứng dụng thật, không qua kho ứng dụng.
 *
 * Android/Chrome: trình duyệt bắn `beforeinstallprompt` → giữ lại, hiện nút trong menu tài khoản.
 * iPhone/iPad (Safari): không có sự kiện đó → hiện một dòng hướng dẫn "Chia sẻ → Thêm vào MH chính".
 */
import { useEffect, useState } from 'react'
import { batKiemBanMoi, layVanTay, nenTuTaiLai, xacNhanSauThongBao } from './ban-moi.ts'
import { GIOI_HAN_GET_MS, henThat } from './tu-phuc-hoi.ts'

/** Sự kiện `beforeinstallprompt` — chưa có trong kiểu DOM chuẩn của TypeScript. */
interface SuKienCaiDat extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let loiMoi: SuKienCaiDat | null = null
const nguoiNghe = new Set<() => void>()
const bao = () => {
  for (const f of nguoiNghe) f()
}

function laSuKienCaiDat(e: Event): e is SuKienCaiDat {
  return 'prompt' in e && typeof (e as { prompt?: unknown }).prompt === 'function'
}

/** Đã thấy bản mới (bộ kiểm trong `ban-moi.ts` báo). Một chiều: đã hiện thì giữ tới khi tải lại. */
let coBanMoi = false
const nguoiNgheBanMoi = new Set<() => void>()

/** Tab ĐANG ẨN và không có tờ / ô nhập dở thì tải lại cho nạp bản mới; kiểm lại NGAY lúc tải vì người dùng có thể vừa quay lại. */
function taiLaiKhiAn(): void {
  if (nenTuTaiLai(document)) location.reload()
}

export function khoiDongPwa(): void {
  if (typeof window === 'undefined') return
  window.addEventListener('beforeinstallprompt', (e) => {
    // Không để Chrome tự hiện dải mời cài lúc lễ tân đang nhận khách — để nút trong menu làm việc đó.
    e.preventDefault()
    if (laSuKienCaiDat(e)) {
      loiMoi = e
      bao()
    }
  })
  window.addEventListener('appinstalled', () => {
    loiMoi = null
    bao()
  })
  // Chỉ bản chạy thật: ở máy phát triển, service worker giữ bản cũ làm sửa code không thấy đổi.
  if (import.meta.env.PROD && 'serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').catch(() => undefined)
    })
  }
  // Báo bản mới (chỉ bản chạy thật). Vân tay bản đang chạy lấy MỘT lần ngay đây, trước khi React chèn gì vào <head>.
  if (import.meta.env.PROD) {
    const vanTayChay = layVanTay(document.head.innerHTML)
    const baoBanMoi = () => {
      coBanMoi = true
      for (const f of nguoiNgheBanMoi) f()
    }
    // Service worker v4 gửi `co-ban-moi` kèm một cổng (ADR-119): trả lời NGAY để sw biết trang tự quyết (sw không `navigate` trang
    // mới), rồi kiểm MỘT lần `index.html`; khác bản đang chạy thì hiện dải + nút, và chỉ tự tải lại khi tab ẨN và không dở việc.
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (ev: MessageEvent) => {
        const d: unknown = ev.data
        if (typeof d !== 'object' || d === null || !('loai' in d) || d.loai !== 'co-ban-moi') return
        ev.ports[0]?.postMessage({ ok: true })
        void xacNhanSauThongBao(vanTayChay, (url, init) => fetch(url, init)).then((khac) => {
          if (!khac) return
          baoBanMoi()
          taiLaiKhiAn()
        })
      })
    }
    batKiemBanMoi(henThat, {
      vanTayChay,
      hen: henThat,
      cuaSo: window,
      taiLieu: {
        addEventListener: (l, f) => document.addEventListener(l, f),
        removeEventListener: (l, f) => document.removeEventListener(l, f),
        get visibilityState() {
          return document.visibilityState
        },
      },
      dongHo: () => performance.now(),
      fetch: (url, init) => fetch(url, init),
      khiCoBanMoi: baoBanMoi,
    })
  }
}

/**
 * Nút "Cập nhật" khi web quầy có bản mới. `co`: đã thấy bản mới; `dang`: đang cập nhật; `loi`: lần bấm vừa rồi
 * chưa có mạng. `capNhat` là chỗ DUY NHẤT tải lại trang — chỉ chạy khi người bấm, không bao giờ tự gọi.
 */
export function useBanMoi(): {
  co: boolean
  dang: boolean
  loi: boolean
  capNhat: () => Promise<void>
} {
  const [, setLan] = useState(0)
  const [dang, setDang] = useState(false)
  const [loi, setLoi] = useState(false)
  useEffect(() => {
    const f = () => setLan((n) => n + 1)
    nguoiNgheBanMoi.add(f)
    return () => {
      nguoiNgheBanMoi.delete(f)
    }
  }, [])

  async function capNhat() {
    // Chốt phòng hờ: đang mở tờ (biểu mẫu dở) thì không đụng tới.
    if (document.querySelector('[aria-modal="true"]')) return
    setDang(true)
    setLoi(false)
    try {
      const res = await fetch('/index.html', {
        cache: 'no-store',
        credentials: 'same-origin',
        signal: AbortSignal.timeout(GIOI_HAN_GET_MS),
      })
      if (!res.ok) throw new Error('index')
    } catch {
      setDang(false)
      setLoi(true)
      return
    }
    try {
      const reg = await navigator.serviceWorker?.getRegistration()
      await reg?.update()
    } catch {
      // Không cập nhật được service worker cũng không sao: tải lại vẫn lấy trang mới từ mạng.
    }
    location.reload()
  }

  return { co: coBanMoi, dang, loi, capNhat }
}

export function dangChayNhuUngDung(): boolean {
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true
  return iosStandalone || window.matchMedia('(display-mode: standalone)').matches
}

export function laIos(): boolean {
  // iPadOS 13+ tự xưng là Mac — nhận ra bằng màn cảm ứng.
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1)
  )
}

/**
 * `'nut'`: có thể mời cài bằng một nút. `'ios'`: phải hướng dẫn tay. `null`: đã cài, hoặc trình duyệt
 * không hỗ trợ — không hiện gì cả.
 */
export function useCaiDat(): { cach: 'nut' | 'ios' | null; cai: () => Promise<void> } {
  const [, setLan] = useState(0)
  useEffect(() => {
    const f = () => setLan((n) => n + 1)
    nguoiNghe.add(f)
    return () => {
      nguoiNghe.delete(f)
    }
  }, [])

  const cach = dangChayNhuUngDung() ? null : loiMoi !== null ? 'nut' : laIos() ? 'ios' : null

  async function cai() {
    const e = loiMoi
    if (e === null) return
    await e.prompt()
    await e.userChoice.catch(() => undefined)
    // Lời mời chỉ dùng được một lần, dù khách đồng ý hay không.
    loiMoi = null
    bao()
  }

  return { cach, cai }
}
